/**
 * The tenant-level chat, at the top of the models list.
 *
 * The same conversation the model pages carry, in the place this page needs it. Inside
 * a model you are working on the page and consulting the rail beside it, so it docks
 * and stays narrow. On the models list the only other action is choosing a model, so
 * the question IS the work and it takes the width.
 *
 * Answers render through the SAME `Markdown` the model rail uses rather than a second
 * copy: an agent writes lists, tables and `studio:` references, and that component
 * carries three guards that each cost a real bug once.
 *
 * Two states. Empty, it is an invitation: a title, an input, and the questions worth
 * asking. Once anything has been asked it is a transcript with a composer under it,
 * because the second question is usually a follow-up and a surface that throws the
 * first answer away to show the second cannot be followed up at all.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Handoff, handoffFrom } from '../model/askBus';
import { Choices, parseChoices } from '../model/choicesBlock';
import { dropEmptyTurn, isAbort } from './askTurns';
import { Markdown } from '../model/ChatMarkdown';
import { CopyAnswer } from '../model/CopyAnswer';
import { flattenStudioLinks } from '../model/answerText';
import {
  fetchDeliverableContent,
  fetchMemoryEnabled,
  getTranscript,
  isModelsRefreshActivity,
  jumpFromActivity,
  listDeliverables,
  listHomeThreads,
  startTurn,
  streamRun,
  SUGGESTED_STEP,
  ThreadRow,
  UiActionEvent,
} from '../model/rgipChatApi';
import { ChatTracker, trackTurn } from '../model/chatAnalytics';
import { whenReadable } from '../model/sessionTime';
import { pendingChoiceKey, replayHomeTurns } from './homeSession';
import { Icon } from '../ui/icon';
import { MemoryBanner } from '../model/MemoryBanner';
import { MemoryDrawer } from '../model/MemoryDrawer';
import { MemoryChange } from '../model/MemoryItem';
import { bannerProposals } from '../model/memoryApi';
import { useMemory } from '../model/useMemory';
import {
  fmtDur,
  liveStatus,
  LiveStatus,
  ToolRun,
  ToolTrail,
} from '../model/ToolTrail';
import { splitModelLinks } from './modelLinks';
import { emitModelsRefresh } from './modelsRefreshBus';
import { TENANT_QUESTIONS } from './tenantQuestions';
import './askDataStudio.css';

/** The agent behind this surface. Tenant-scoped, read-only, its own catalog entry. */
const AGENT = 'studio-home';

/**
 * A thread per CONVERSATION, not per tenant.
 *
 * It was per tenant and stable across reloads, so a follow-up kept its history. That
 * history is also the failure: asked whether a model existed, this agent answered
 * wrongly once, and every later turn in the thread then agreed with it, answering from
 * its own earlier claim with no tool call at all. A wrong answer became the record.
 *
 * Minted on mount and again on "New question", so a reset is a reset on the server too
 * rather than only on screen. Follow-ups still work: the id is held for the life of the
 * conversation, which is what a follow-up is.
 *
 * A uuid, like the model rail's, not a millisecond timestamp: two tabs opened in the
 * same millisecond shared a conversation. The tenant is not in it either - the
 * checkpoint key is already `{tenant}:{thread_id}` server-side, so putting it here
 * said it twice and implied a scoping this id does not do.
 */
const newThread = () => `studio-home:${crypto.randomUUID()}`;

interface Turn {
  role: 'user' | 'agent';
  text: string;
  /** Identity, so a turn is found without counting. See `askTurns`. */
  id: number;
  /** Jump affordances the agent surfaced during this turn (ADR 0010). */
  jumps?: UiActionEvent[];
  /**
   * A decision handed back to the reader, as buttons.
   *
   * Every write this agent can make previews first, and the preview is only worth
   * anything if saying yes to it is one press. A question in prose leaves them typing
   * "yes" to something the agent already knew the options to.
   */
  choices?: Choices;
  /** The steps the agent took this turn, for the tool trail. */
  tools?: ToolRun[];
  /** When the answer turn began, and how long it took once finished. */
  startedAt?: number;
  elapsedMs?: number;
}

interface AskDataStudioProps {
  /** The tenant. Absent means the surface cannot be used, and it sits idle. */
  tenant?: string;
  /**
   * Open a model, carrying what got us there.
   *
   * A question about ONE model is answered by that model's own assistant, which has
   * the tools to read its trees, signals and stats. Handing the question over means
   * the reader does not type it twice: they have already said what they want. The
   * answer that named the model goes too, so the assistant on the other side knows
   * why anyone is there rather than meeting them cold.
   */
  onOpenModel?: (modelId: string, handoff: Handoff) => void;
  /** Sends the chat's Segment events (`chatAnalytics`). Optional: no host, no events. */
  onTrack?: ChatTracker;
}

export function AskDataStudio({
  tenant,
  onOpenModel,
  onTrack,
}: AskDataStudioProps) {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [threadId, setThreadId] = useState(() => newThread());
  // The last thing the reader actually TYPED, and empty once they press a button
  // instead. Half of what a handoff carries; the other half is the answer that named
  // the model, read off `turns` at the moment of the handoff.
  //
  // Still not the whole transcript: the model's assistant reads the model itself, and
  // a tenant-level conversation about seventy models is a second, staler source for a
  // thread pinned to one of them.
  const lastAsked = useRef('');
  const [question, setQuestion] = useState('');
  const [running, setRunning] = useState(false);
  // Ticks while a turn runs, for the live durations in the trail and the footer.
  const [now, setNow] = useState(0);
  const [failed, setFailed] = useState<string | null>(null);
  const abort = useRef<AbortController | null>(null);
  /**
   * This reader's earlier conversations with this assistant, newest first.
   *
   * Offered rather than resumed. Arriving at the models list is usually the start of
   * something, not the middle of what was asked yesterday, and a page that reopens an
   * old conversation by itself is one somebody has to close before they can think.
   */
  const [sessions, setSessions] = useState<ThreadRow[]>([]);
  const [showSessions, setShowSessions] = useState(false);
  /**
   * The assistant's Memory for the whole org, behind the AI flag. Off,
   * `memoryOn` stays false and `showMemory` can never be set, so every condition below
   * that mentions them reads as it did before Memory existed.
   */
  const [memoryOn, setMemoryOn] = useState(false);
  // The flag read in flight; a turn sent before it settles waits for it.
  const memoryFlag = useRef<Promise<boolean>>(Promise.resolve(false));
  const [showMemory, setShowMemory] = useState(false);
  // studio-home holds `memory_propose`, so a turn here can leave a TENANT-wide proposal
  // for the banner. Re-read when a turn ends and whenever the Memory view is opened.
  const [memoryTick, setMemoryTick] = useState(0);
  const [memoryNotice, setMemoryNotice] = useState<string | null>(null);
  const memory = useMemory(tenant ?? '', null, memoryOn, memoryTick);
  const reloadMemory = memory.reload;
  const onMemoryChanged = useCallback(
    (change: MemoryChange) => {
      reloadMemory();
      setMemoryNotice(change.notice ?? null);
    },
    [reloadMemory]
  );
  /** The `choices` block being streamed, if one is. */
  const block = useRef('');
  // Monotonic, never reused, so an id identifies one turn for the life of the
  // component even across a cleared transcript.
  const nextTurnId = useRef(1);
  const tail = useRef<HTMLDivElement>(null);

  // Follows the answer as it streams, but only once a conversation exists, or the
  // page scrolls itself on load to an empty transcript.
  useEffect(() => {
    if (turns.length) tail.current?.scrollIntoView({ block: 'nearest' });
  }, [turns, running]);

  useEffect(() => () => abort.current?.abort(), []);

  // What this reader asked before, per tenant and per user (`mine` is resolved from
  // the verified token server-side). Best-effort: no list means no offer, never a
  // page that fails to render.
  useEffect(() => {
    if (!tenant) return;
    let live = true;
    void listHomeThreads(tenant, AGENT).then((rows) => {
      if (live) setSessions(rows);
    });
    return () => {
      live = false;
    };
  }, [tenant]);

  useEffect(() => {
    if (!tenant) return undefined;
    let live = true;
    memoryFlag.current = fetchMemoryEnabled(tenant).catch(() => false);
    void memoryFlag.current.then((on) => {
      if (live) setMemoryOn(on);
    });
    return () => {
      live = false;
    };
  }, [tenant]);

  useEffect(() => {
    if (!running) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, [running]);

  // A turn that just ended may have proposed something; so may a session review while
  // the view was closed. `ranTurn` keeps the first render from counting as a turn.
  const ranTurn = useRef(false);
  useEffect(() => {
    if (running) ranTurn.current = true;
    else if (ranTurn.current && memoryOn) setMemoryTick((t) => t + 1);
  }, [running, memoryOn]);
  useEffect(() => {
    if (showMemory) setMemoryTick((t) => t + 1);
    setMemoryNotice(null);
  }, [showMemory]);

  // Reopen one. The thread id is adopted, so the next question continues it on the
  // server rather than starting a parallel conversation that only looks continuous.
  function openSession(id: string) {
    if (!tenant) return;
    abort.current?.abort();
    setShowSessions(false);
    setThreadId(id);
    setTurns([]);
    setFailed(null);
    setRunning(false);
    void getTranscript(tenant, id)
      .then(async (messages) => {
        // A replayed transcript needs ids too, and they come from the same
        // counter so a reopened conversation can never collide with a live turn.
        setTurns(
          replayHomeTurns(messages).map((turn) => ({
            ...turn,
            id: nextTurnId.current++,
          }))
        );
        // A conversation can end on a decision. rgip keeps the block's body out of
        // the stored prose, so the buttons are rebuilt from the deliverable it
        // references. Best-effort: without it the question is still there as text and
        // can be answered by typing, which is worse than buttons and much better than
        // a panel that failed to open.
        const key = pendingChoiceKey(messages);
        if (!key) return;
        try {
          const stored = await listDeliverables(tenant, id);
          const row = stored.find((d) => d.artifact_key === key);
          if (!row) return;
          const parsed = parseChoices(
            await fetchDeliverableContent(tenant, row.deliverable_id)
          );
          if (parsed) {
            onLastAgentTurn((turn) => ({ ...turn, choices: parsed }));
          }
        } catch {
          // The prose is already on screen; the buttons are the part we can lose.
        }
      })
      .catch(() => setFailed('That conversation could not be reopened.'));
  }

  // What to hand a model page. The rule itself lives in `askBus` where it can be
  // tested; this only supplies what the component knows.
  const handoffFor = () => handoffFrom(turns, lastAsked.current);

  /** Rewrite the last agent turn, which is the only one anything ever changes. */
  function onLastAgentTurn(change: (turn: Turn) => Turn) {
    setTurns((all) => {
      const at = all.map((t) => t.role).lastIndexOf('agent');
      return at === -1 ? all : all.map((t, i) => (i === at ? change(t) : t));
    });
  }

  async function ask(
    message: string,
    { typed = true }: { typed?: boolean } = {}
  ) {
    const text = message.trim();
    if (!text || !tenant || running) return;

    setQuestion('');
    // A pressed button CLEARS this rather than setting it. What a handoff carries is
    // a question about a model, and a decision about the list is not one: carried
    // over, "Yes, archive models 64 through 70" was asked of model 44's assistant,
    // which can only explain that archiving is not something it can do.
    lastAsked.current = typed ? text : '';
    setFailed(null);
    setRunning(true);
    // THIS turn's answer, identified before anything is appended. Everything
    // below refers to it by id rather than as "the last turn": by the time a
    // failure arrives the reader may have asked something else, and the last
    // turn is then somebody else's. An index would not do either, because one
    // captured inside the updater is not assigned until React runs it.
    const answerId = nextTurnId.current + 1;
    setTurns((all) => [
      ...all,
      { role: 'user', text, id: nextTurnId.current },
      { role: 'agent', text: '', id: answerId, startedAt: Date.now() },
    ]);
    nextTurnId.current += 2;

    // Only the answer turn is ever rewritten, and it is always the last one, so a
    // streamed delta cannot land on a turn the reader has already read.
    const onAgent = (change: (turn: Turn) => Turn) =>
      setTurns((all) =>
        all.map((turn) => (turn.id === answerId ? change(turn) : turn))
      );

    abort.current?.abort();
    abort.current = new AbortController();
    let failed = false;
    let aborted = false;
    const tracked = trackTurn(
      onTrack,
      {
        tenantId: tenant,
        modelId: null,
        agent: AGENT,
        sessionId: threadId,
        surface: 'home',
      },
      text
    );
    try {
      const runId = await startTurn(tenant, threadId, null, text, {
        agent: AGENT,
        memory: await memoryFlag.current,
        // A pressed `choices` option is the only caller with typed=false.
        origin: typed ? undefined : SUGGESTED_STEP,
      });
      tracked.ran(runId);
      await streamRun(
        tenant,
        runId,
        {
          onToken: (delta) =>
            onAgent((turn) => ({ ...turn, text: turn.text + delta })),
          // An error frame ends the run without throwing. Nothing on screen reacts to
          // it here; it only keeps the turn from being counted as a success.
          onError: () => {
            failed = true;
          },
          onActivityStart: (event) => {
            // A jump is a card under the answer, never a step.
            if (jumpFromActivity(event)) return;
            onAgent((turn) => ({
              ...turn,
              tools: [
                ...(turn.tools ?? []),
                { id: event.id, label: event.label, startedAt: Date.now() },
              ],
            }));
          },
          // A `choices` block arrives as its own artifact, whole (rgip
          // `WHOLE_BLOCK_KEYS`). Collected here and drawn under the answer.
          onArtifactStart: (event) => {
            if (event.key === 'choices') block.current = '';
          },
          onArtifactDelta: (event) => {
            if (event.key === 'choices') block.current += event.delta;
          },
          onArtifactEnd: (event) => {
            if (event.key !== 'choices') return;
            const parsed = parseChoices(block.current);
            block.current = '';
            if (parsed) onAgent((turn) => ({ ...turn, choices: parsed }));
          },
          onActivityEnd: (event) => {
            onAgent((turn) => {
              const tools = [...(turn.tools ?? [])];
              for (let i = tools.length - 1; i >= 0; i--) {
                const match = event.id ? tools[i].id === event.id : true;
                if (match && tools[i].endedAt == null) {
                  tools[i] = { ...tools[i], endedAt: Date.now() };
                  break;
                }
              }
              return { ...turn, tools };
            });
            // The agent just changed a model through the ordinary routes, so the
            // list around this chat is stale and nothing else would tell it.
            if (isModelsRefreshActivity(event)) emitModelsRefresh();
            const jump = jumpFromActivity(event);
            if (jump) {
              onAgent((turn) => ({
                ...turn,
                jumps: [...(turn.jumps ?? []), jump],
              }));
            }
          },
        },
        abort.current.signal
      );
    } catch (error) {
      // Asking a follow-up aborts this stream on purpose, so an abort is not a
      // failure and must not be reported as one. Without this, a second
      // question told the reader their first one had broken.
      if (isAbort(error)) {
        aborted = true;
        return;
      }
      failed = true;
      // What it means for them, not which of our processes refused.
      setFailed(
        error instanceof Error && error.message
          ? error.message
          : 'That question could not be answered just now.'
      );
      // An answer turn with nothing in it is noise once the failure is stated,
      // but only THIS turn's. See `dropEmptyTurn`.
      setTurns((all) => dropEmptyTurn(all, answerId));
    } finally {
      tracked.ended({ stopped: aborted, failed });
      onAgent((turn) =>
        turn.startedAt != null
          ? { ...turn, elapsedMs: Date.now() - turn.startedAt }
          : turn
      );
      setRunning(false);
      // The row this conversation is listed by is written BY the turn, so the list
      // loaded on arrival never contains the conversation being had. Refetched here
      // rather than in the mount effect, which is what left "My sessions" hidden
      // through a whole first conversation and appearing only on the next load.
      void listHomeThreads(tenant, AGENT).then(setSessions);
    }
  }

  const started = turns.length > 0;

  const composer = (
    <form
      className="mhask-box"
      onSubmit={(event) => {
        event.preventDefault();
        void ask(question);
      }}
      role="search"
    >
      <label className="mhask-sr" htmlFor="ask-data-studio">
        Ask a question
      </label>
      <input
        id="ask-data-studio"
        type="text"
        autoComplete="off"
        value={question}
        disabled={!tenant}
        onChange={(event) => setQuestion(event.target.value)}
        placeholder={
          started
            ? 'Ask a follow-up...'
            : 'Ask about your models, deployments or CRM push...'
        }
      />
      {/* Inside a conversation the same "Send" the model chat has. The landing box
          keeps its arrow: it is a search field there, not a composer. */}
      {started ? (
        <button
          className="mhask-send"
          type="submit"
          disabled={running || !question.trim() || !tenant}
        >
          Send
        </button>
      ) : (
        <button
          className="mhask-go"
          type="submit"
          aria-label="Ask"
          disabled={running || !question.trim() || !tenant}
        >
          <Icon
            icon="far fa-arrow-left"
            style={{ transform: 'rotate(180deg)' }}
          />
        </button>
      )}
    </form>
  );

  // Tenant-scope proposals are drawn in full: a model's proposal is decided on that
  // model's chat. Those still waiting get one quiet line instead of nothing, because a
  // reviewer opening Ask Data Studio otherwise had no sign anything was there: the
  // Memory view listed three while the chat showed none.
  const waitingOnModels = bannerProposals(memory.list, 'model').length;
  const memoryBanner = memoryOn && tenant && (
    <>
      {memoryNotice && <p className="mem-detail mem-notice">{memoryNotice}</p>}
      <MemoryBanner
        tenant={tenant}
        list={memory.list}
        scope="tenant"
        onChanged={onMemoryChanged}
      />
      {waitingOnModels > 0 && (
        <p className="mem-nudge" data-testid="memory-nudge">
          {waitingOnModels === 1
            ? '1 decision on a model is waiting for approval'
            : `${waitingOnModels} decisions on your models are waiting for approval`}
          <button
            type="button"
            className="mem-nudge-open"
            onClick={() => setShowMemory(true)}
          >
            Review
          </button>
        </p>
      )}
    </>
  );

  return (
    <section
      className={`mhask${started ? ' mhask--open' : ''}`}
      aria-label="Ask Data Studio"
    >
      {!started && !showSessions && !showMemory && (
        <div className="mhask-hero">
          {/* Top right, where the chat header keeps the same control. The landing
              state has no header bar of its own - the card IS the invitation, and a
              bar would repeat the title under it - so this row carries the control
              alone. What matters is that the way back to a conversation is in the
              same corner whichever state you are in. */}
          {(sessions.length > 0 || memoryOn) && (
            <div className="mhask-hero-actions">
              {sessions.length > 0 && (
                <button
                  type="button"
                  className="mhask-clear"
                  onClick={() => setShowSessions(true)}
                >
                  My sessions ({sessions.length})
                </button>
              )}
              {memoryOn && (
                <button
                  type="button"
                  className="mhask-clear mem-ads-btn"
                  data-testid="ask-memory"
                  title="Decisions the assistant follows across your models"
                  onClick={() => setShowMemory(true)}
                >
                  Memory
                </button>
              )}
            </div>
          )}
          <h2 className="mhask-title">Ask Data Studio</h2>
          <p className="mhask-sub">
            Questions that span every model in this org, not just the one you
            have open.
          </p>
          {memoryBanner}
          {composer}
          {/* These SEND. They used to answer from the rows with no model run,
              which made the four most prominent controls on a surface called Ask
              Data Studio the only ones that never asked it anything. */}
          <div className="mhask-pills">
            {TENANT_QUESTIONS.map(({ id, question: prompt }) => (
              <button
                key={id}
                type="button"
                className="mhask-pill"
                disabled={!tenant}
                onClick={() => void ask(prompt)}
              >
                {prompt}
              </button>
            ))}
          </div>
        </div>
      )}

      {(started || showSessions || showMemory) && (
        <div className="mhask-chat">
          <div className="mhask-head">
            <span className="mhask-head-title">Ask Data Studio</span>
            {/* One group, one auto margin. Two auto margins in a flex row do not
                stack, they SPLIT the free space, so a margin on each button left
                them at opposite ends of the header and moved both whenever either
                one appeared or went away. */}
            <div className="mhask-head-actions">
              {sessions.length > 0 && !running && !showMemory && (
                <button
                  type="button"
                  className="mhask-clear"
                  aria-expanded={showSessions}
                  onClick={() => setShowSessions((open) => !open)}
                >
                  {showSessions ? 'Back' : 'My sessions'}
                </button>
              )}
              {memoryOn && !running && (
                <button
                  type="button"
                  className="mhask-clear"
                  aria-expanded={showMemory}
                  title="Decisions the assistant follows across your models"
                  onClick={() => {
                    setShowSessions(false);
                    setShowMemory((open) => !open);
                  }}
                >
                  {showMemory ? 'Back' : 'Memory'}
                </button>
              )}
              {started && (
                <button
                  type="button"
                  className="mhask-clear"
                  onClick={() => {
                    abort.current?.abort();
                    // A new thread, not just an empty screen: the server keeps the
                    // old one, and reusing its id is how a discarded answer comes
                    // back as the premise of the next one.
                    setThreadId(newThread());
                    setTurns([]);
                    setFailed(null);
                    setRunning(false);
                  }}
                >
                  New question
                </button>
              )}
            </div>
          </div>

          {/* Earlier conversations REPLACE the transcript rather than sitting above
              it: it is somewhere you go and come back from, and the control that
              opened it says Back while you are there. Inside the panel chrome, so
              the header stays put instead of sliding down past the list. */}
          {showSessions && (
            <div className="mhask-sessions">
              {sessions.map((session) => (
                <button
                  type="button"
                  key={session.thread_id}
                  className="mhask-session"
                  onClick={() => openSession(session.thread_id)}
                >
                  <span className="mhask-session-title">
                    {session.title?.trim() || 'Untitled conversation'}
                  </span>
                  {session.preview?.trim() && (
                    <span className="mhask-session-preview">
                      {session.preview}
                    </span>
                  )}
                  <span className="mhask-session-when">
                    {whenReadable(session.updated_at)}
                  </span>
                </button>
              ))}
            </div>
          )}

          {showMemory && tenant && (
            <>
              {memoryNotice && (
                <p className="mem-detail mem-notice">{memoryNotice}</p>
              )}
              <MemoryDrawer
                tenant={tenant}
                modelId={null}
                list={memory.list}
                error={memory.error}
                onChanged={onMemoryChanged}
                className="mem-drawer--wide"
              />
            </>
          )}

          {!showSessions && !showMemory && memoryBanner}

          {/* The transcript and the composer belong to a conversation. Browsing the
              list from the landing page has neither, so the panel is the header and
              the list and nothing else. */}
          {!showSessions && !showMemory && (
            <div className="mhask-turns">
              {turns.map((turn, index) => (
                <div
                  // By id, not index. `dropEmptyTurn` removes a turn from the
                  // MIDDLE of this list, and with an index key React reuses the
                  // node at that position for whatever slid into it: the removed
                  // turn's rendered content is then attached to its neighbour.
                  key={turn.id}
                  className={`mhask-turn mhask-turn--${turn.role}`}
                >
                  {turn.role === 'user' ? (
                    <span className="mhask-bubble">{turn.text}</span>
                  ) : (
                    <>
                      {(turn.tools?.length ?? 0) > 0 && (
                        <ToolTrail tools={turn.tools as ToolRun[]} now={now} />
                      )}
                      {turn.text ? (
                        <div className="mhask-prose">
                          <Markdown
                            text={splitModelLinks(turn.text).prose}
                            onFocus={(targetType, targetId) => {
                              if (targetType === 'model') {
                                onOpenModel?.(targetId, handoffFor());
                              }
                            }}
                          />
                        </div>
                      ) : (
                        running &&
                        index === turns.length - 1 &&
                        (() => {
                          const status = liveStatus(turn.tools ?? [], false);
                          return status && <LiveStatus label={status} />;
                        })()
                      )}
                      {/* The decision, as buttons. A write here always previews
                        first, and a preview is only worth having if saying yes to
                        it is one press. Pressing sends the option's `message` as
                        an ordinary turn, so a button is feedback to the agent and
                        never a tool call of its own.

                        ONLY on the last turn, which is the whole guard. `!running`
                        alone left every past turn's buttons live forever, and a
                        button that sends its message verbatim is a button that
                        re-sends a decision the reader already made: on 2026-09-22
                        "Yes, archive models 64 through 70" was sent twice, six
                        minutes apart, the second time against models that were
                        already archived. Nothing was written twice, but only
                        because the agent noticed and covered for the page.

                        The same rule the model-page panel arrived at
                        (`canChoose` in ModelChatPanel): a decision belongs to the
                        turn that offered it, and once the conversation has moved
                        past that turn the decision has been made. */}
                      {turn.choices &&
                        !running &&
                        index === turns.length - 1 && (
                          <div className="mhask-choices">
                            {!!turn.choices.question && (
                              <span className="mhask-choices-q">
                                {turn.choices.question}
                              </span>
                            )}
                            <div className="mhask-choices-row">
                              {turn.choices.options.map((option) => (
                                <button
                                  key={option.message}
                                  type="button"
                                  className="mhask-choice"
                                  onClick={() =>
                                    void ask(option.message, { typed: false })
                                  }
                                >
                                  {option.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                      {/* Every model the answer named, as a card under it. The
                        label stays in the sentence; this is the thing you press.
                        `jumps` covers a `studio_focus` activity, which this agent
                        no longer emits but which costs nothing to keep rendering. */}
                      {(() => {
                        const named = splitModelLinks(turn.text).models;
                        const chips = [
                          ...named.map((model) => ({
                            key: `m-${model.id}`,
                            id: model.id,
                            label: model.label,
                          })),
                          ...(turn.jumps ?? []).map((jump) => ({
                            key: `j-${jump.target_id}`,
                            id: jump.target_id,
                            label: jump.label || `Open model ${jump.target_id}`,
                          })),
                        ];
                        if (!chips.length) return null;
                        return (
                          <div className="mhask-cards">
                            {chips.map((chip) => (
                              <button
                                key={chip.key}
                                type="button"
                                className="mhask-card"
                                onClick={() =>
                                  onOpenModel?.(chip.id, handoffFor())
                                }
                              >
                                <span className="mhask-card-name">
                                  {chip.label}
                                </span>
                                <span className="mhask-card-go">
                                  Open, and take this question along
                                  <span aria-hidden> &rarr;</span>
                                </span>
                              </button>
                            ))}
                          </div>
                        );
                      })()}

                      {/* Copy the answer, the same button the model chat has. On a
                        finished turn only: mid-stream the answer is half-written. The
                        prose as shown, with Studio's own links reduced to their words,
                        which is what the model chat copies too. */}
                      {turn.text &&
                        !(running && index === turns.length - 1) && (
                          <div className="mhask-foot">
                            <CopyAnswer
                              text={flattenStudioLinks(
                                splitModelLinks(turn.text).prose
                              ).trim()}
                            />
                            {turn.elapsedMs != null && (
                              <span className="mhask-elapsed">
                                ⏱ {fmtDur(turn.elapsedMs)}
                              </span>
                            )}
                          </div>
                        )}
                    </>
                  )}
                </div>
              ))}
              {failed && <p className="mhask-failed">{failed}</p>}
              <div ref={tail} />
            </div>
          )}

          {!showSessions && !showMemory && (
            <div className="mhask-composer">
              {composer}
              <p className="mhask-disclaimer">
                AI can make mistakes. Always verify
              </p>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export default AskDataStudio;
